import React, { useState, useEffect } from 'react';
import { Pencil, Plus, X } from 'lucide-react';
import { Season, League } from '../../types/sports';

interface SeasonFormProps {
  season?: Season | null;
  leagues: League[];
  onSave: (data: {
    name: string;
    startDate: string;
    endDate: string;
    leagueId: number;
    isActive?: boolean;
    isCurrent?: boolean;
  }) => void;
  onCancel: () => void;
  loading?: boolean;
}

const SeasonForm: React.FC<SeasonFormProps> = ({
  season,
  leagues,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    leagueId: 0,
    isActive: true,
    isCurrent: false
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (season) {
      setFormData({
        name: season.name || '',
        startDate: season.startDate ? new Date(season.startDate).toISOString().split('T')[0] : '',
        endDate: season.endDate ? new Date(season.endDate).toISOString().split('T')[0] : '',
        leagueId: season.leagueId || 0,
        isActive: season.isActive !== undefined ? season.isActive : true,
        isCurrent: season.isCurrent !== undefined ? season.isCurrent : false
      });
    }
  }, [season]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'leagueId' ? parseInt(value) : value)
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Startdatum ist erforderlich';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'Enddatum ist erforderlich';
    }

    if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
      newErrors.endDate = 'Enddatum muss nach dem Startdatum liegen';
    }

    if (formData.leagueId === 0) {
      newErrors.leagueId = 'Liga ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        name: formData.name.trim(),
        startDate: formData.startDate,
        endDate: formData.endDate,
        leagueId: formData.leagueId,
        isActive: formData.isActive,
        isCurrent: formData.isCurrent
      };
      onSave(submitData);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={modalHeaderStyle}>
          <h2 style={modalTitleStyle}>
            {season ? <><Pencil size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Saison bearbeiten</> : <><Plus size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Neue Saison</>}
          </h2>
          <button onClick={onCancel} style={closeButtonStyle} disabled={loading}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>
              Name <span style={requiredStyle}>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={errors.name ? { ...inputStyle, ...errorInputStyle } : inputStyle}
              placeholder="z.B. Saison 2024/2025"
              disabled={loading}
            />
            {errors.name && <span style={errorTextStyle}>{errors.name}</span>}
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>
              Liga <span style={requiredStyle}>*</span>
            </label>
            <select
              name="leagueId"
              value={formData.leagueId}
              onChange={handleChange}
              style={errors.leagueId ? { ...selectStyle, ...errorInputStyle } : selectStyle}
              disabled={loading}
            >
              <option value={0}>Liga auswählen...</option>
              {leagues.map(league => (
                <option key={league.id} value={league.id}>
                  {league.sport?.icon} {league.name} ({league.country?.flag} {league.country?.name})
                </option>
              ))}
            </select>
            {errors.leagueId && <span style={errorTextStyle}>{errors.leagueId}</span>}
          </div>

          <div style={formRowStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Startdatum <span style={requiredStyle}>*</span>
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                style={errors.startDate ? { ...inputStyle, ...errorInputStyle } : inputStyle}
                disabled={loading}
              />
              {errors.startDate && <span style={errorTextStyle}>{errors.startDate}</span>}
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Enddatum <span style={requiredStyle}>*</span>
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                style={errors.endDate ? { ...inputStyle, ...errorInputStyle } : inputStyle}
                disabled={loading}
              />
              {errors.endDate && <span style={errorTextStyle}>{errors.endDate}</span>}
            </div>
          </div>

          {/* Status Options - only show when editing */}
          {season && (
            <div style={formGroupStyle}>
              <label style={labelStyle}>Status</label>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    disabled={loading}
                    style={{ margin: 0 }}
                  />
                  <label htmlFor="isActive" style={{ margin: 0, fontSize: '0.9rem' }}>
                    Aktiv
                  </label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="isCurrent"
                    name="isCurrent"
                    checked={formData.isCurrent}
                    onChange={handleChange}
                    disabled={loading}
                    style={{ margin: 0 }}
                  />
                  <label htmlFor="isCurrent" style={{ margin: 0, fontSize: '0.9rem' }}>
                    Aktuelle Saison
                  </label>
                </div>
              </div>
            </div>
          )}

          <div style={formActionsStyle}>
            <button
              type="button"
              onClick={onCancel}
              style={cancelButtonStyle}
              disabled={loading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              style={submitButtonStyle}
              disabled={loading}
            >
              {loading ? 'Wird gespeichert...' : (season ? 'Aktualisieren' : 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '90%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
};

const modalHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1.5rem',
  borderBottom: '1px solid #e5e7eb',
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#1f2937',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: '#6b7280',
  padding: '0.25rem',
  lineHeight: 1,
};

const formStyle: React.CSSProperties = {
  padding: '1.5rem',
};

const formGroupStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
  flex: 1,
};

const formRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.5rem',
  fontWeight: '500',
  color: '#374151',
};

const requiredStyle: React.CSSProperties = {
  color: '#ef4444',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '1rem',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
};

const errorInputStyle: React.CSSProperties = {
  borderColor: '#ef4444',
};

const errorTextStyle: React.CSSProperties = {
  color: '#ef4444',
  fontSize: '0.875rem',
  marginTop: '0.25rem',
  display: 'block',
};

const formActionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
  marginTop: '2rem',
  paddingTop: '1rem',
  borderTop: '1px solid #e5e7eb',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: 'white',
  color: '#374151',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#3b82f6',
  color: 'white',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
};

export default SeasonForm;

